import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import Editor from '@monaco-editor/react';
import mermaid from 'mermaid';
import indexedDbFileStorage, {GIT_STATE, PREVIEW_STATE} from '../utils/indexedDbFileStorage';
import {
  applyUnifiedDiff,
  parseUnifiedDiff
} from '../utils/diffParser';
import MonacoSearchPanel from './MonacoSearchPanel';
import DiffParserLoader from './DiffParserLoader';
import ConflictResolutionModal from './ConflictResolutionModal';
import useCollaboration from '../hooks/useCollaboration';
import PipelineEditor from './PipelineEditor';
import './MonacoFileEditor.css';

/**
 * Helper function to escape HTML entities
 */
const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Simple markdown to HTML converter
 * Handles basic markdown syntax without external dependencies
 */
const renderMarkdown = (markdown) => {
  if (!markdown) return '';
  
  let html = markdown;
  
  // Extract and protect Mermaid diagrams from processing
  const mermaidBlocks = [];
  html = html.replace(/```mermaid\n([\s\S]*?)```/g, (match, diagram) => {
    const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
    const placeholder = `XMERMAIDBLOCKX${mermaidBlocks.length}XMERMAIDBLOCKX`;
    mermaidBlocks.push({ id, diagram: diagram.trim(), placeholder });
    return placeholder;
  });
  
  // Extract and protect fenced code blocks (```) from processing - non-greedy match
  const codeBlocks = [];
  html = html.replace(/```([^\n]*)\n([\s\S]*?)```/g, (match, lang, code) => {
    const placeholder = `XCODEBLOCKX${codeBlocks.length}XCODEBLOCKX`;
    codeBlocks.push({ content: `<pre><code class="language-${lang.trim()}">${escapeHtml(code)}</code></pre>`, placeholder });
    return placeholder;
  });
  
  // Extract and protect indented code blocks (4+ spaces) from processing
  const indentedCodeBlocks = [];
  const lines = html.split('\n');
  let i = 0;
  while (i < lines.length) {
    // Check if line starts with 4+ spaces (indented code block)
    if (lines[i].match(/^    /)) {
      const codeLines = [];
      // Collect all consecutive indented lines
      while (i < lines.length && (lines[i].match(/^    /) || lines[i].trim() === '')) {
        if (lines[i].match(/^    /)) {
          codeLines.push(lines[i].substring(4)); // Remove 4-space indent
        } else {
          codeLines.push(''); // Preserve empty lines
        }
        i++;
      }
      // Only treat as code block if it has content
      if (codeLines.some(line => line.trim() !== '')) {
        const placeholder = `XINDENTEDBLOCKX${indentedCodeBlocks.length}XINDENTEDBLOCKX`;
        indentedCodeBlocks.push({ 
          content: `<pre><code>${escapeHtml(codeLines.join('\n'))}</code></pre>`, 
          placeholder 
        });
        lines.splice(i - codeLines.length, codeLines.length, placeholder);
        i = i - codeLines.length + 1;
      }
    } else {
      i++;
    }
  }
  html = lines.join('\n');
  
  // Tables - process before other formatting
  const tableRegex = /(\|[^\n]+\|[\r\n]+)+/g;
  html = html.replace(tableRegex, (match) => {
    const rows = match.trim().split('\n').filter(row => row.trim());
    if (rows.length < 2) return match;
    
    let tableHtml = '<table class="markdown-table">';
    
    // Process header row
    const headerCells = rows[0].split('|').filter(cell => cell.trim()).map(cell => cell.trim());
    tableHtml += '<thead><tr>';
    headerCells.forEach(cell => {
      tableHtml += `<th>${cell}</th>`;
    });
    tableHtml += '</tr></thead>';
    
    // Skip separator row (---) and process data rows
    tableHtml += '<tbody>';
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      // Skip separator rows
      if (row.match(/^[\|\s\-:]+$/)) continue;
      
      const cells = row.split('|').filter(cell => cell.trim()).map(cell => cell.trim());
      tableHtml += '<tr>';
      cells.forEach(cell => {
        tableHtml += `<td>${cell}</td>`;
      });
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table>';
    
    return tableHtml;
  });
  
  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
  
  // Bold
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  
  // Italic
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  
  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
  
  // Lists
  html = html.replace(/^\* (.+)$/gim, '<li>$1</li>');
  html = html.replace(/^\- (.+)$/gim, '<li>$1</li>');
  html = html.replace(/^(\d+)\. (.+)$/gim, '<li>$2</li>');
  
  // Wrap list items
  html = html.replace(/(<li>.*<\/li>)/gis, '<ul>$1</ul>');
  
  // Paragraphs (lines separated by double newline) - but protect placeholders
  html = html.split('\n\n').map(block => {
    // Don't wrap placeholders in paragraphs
    if (block.match(/^X(MERMAID|CODE|INDENTED)BLOCKX\d+X(MERMAID|CODE|INDENTED)BLOCKX$/)) {
      return block;
    }
    return `<p>${block}</p>`;
  }).join('');
  
  // Line breaks removed - no br tags
  // html = html.split('\n').map(line => {
  //   // Don't add br to placeholder lines
  //   if (line.match(/^X(MERMAID|CODE|INDENTED)BLOCKX\d+X(MERMAID|CODE|INDENTED)BLOCKX$/)) {
  //     return line;
  //   }
  //   return line;
  // }).join('<br />');
  
  // Restore indented code blocks first
  indentedCodeBlocks.forEach((block) => {
    html = html.replace(block.placeholder, block.content);
  });
  
  // Restore fenced code blocks
  codeBlocks.forEach((block) => {
    html = html.replace(block.placeholder, block.content);
  });
  
  // Restore Mermaid blocks - keep diagram text inside for later rendering
  mermaidBlocks.forEach((block) => {
    html = html.replace(block.placeholder, 
      `<div class="mermaid-wrapper"><div class="mermaid" data-diagram="${escapeHtml(block.diagram)}">${escapeHtml(block.diagram)}</div></div>`);
  });
  
  return html;
};

/**
 * MonacoFileEditor Component - VSCode-like file editor using Monaco Editor
 * Now supports real-time AI-driven content updates and localStorage persistence
 * @param {boolean} isPreviewMode - When true, enables auto-approve mode for seamless preview updates
 */
const MonacoFileEditor = ({file, onSave, readOnly, onContentChange, projectId, serviceName, isPreviewMode = false, onErrorsChange}) => {
  const [value, setValue] = useState('');
  const [modified, setModified] = useState(false);
  const [gitState, setGitState] = useState(null);
  const [previewState, setPreviewState] = useState(null);
  // Initialize theme based on app theme
  const getAppTheme = () => {
    const appTheme = localStorage.getItem('theme') || 'light';
    const isLightTheme = appTheme === 'light' || appTheme === 'monokai' || appTheme === 'catppuccin-latte' || appTheme === 'light-high-contrast';
    return isLightTheme ? 'vs' : 'vs-dark';
  };
  const [theme, setTheme] = useState(getAppTheme());
  const [isAIEditing, setIsAIEditing] = useState(false); // Track if AI is currently editing
  const [errorCount, setErrorCount] = useState(0); // Track error markers
  const [warningCount, setWarningCount] = useState(0); // Track warning markers
  const [isFullscreen, setIsFullscreen] = useState(false); // Track fullscreen state
  const [showSearchPanel, setShowSearchPanel] = useState(false); // Track search panel visibility
  const [showDiffLoader, setShowDiffLoader] = useState(false); // Track diff parser loader
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(false); // Track markdown preview mode
  const [showHtmlPreview, setShowHtmlPreview] = useState(false); // Track HTML preview mode
  const [showSvgPreview, setShowSvgPreview] = useState(false); // Track SVG preview mode
  const [showPipelineEditor, setShowPipelineEditor] = useState(false); // Track pipeline editor mode
  const [diffLoaderFileName, setDiffLoaderFileName] = useState(''); // Track file being processed
  const [diffProgressInfo, setDiffProgressInfo] = useState(null); // Track diff progress details
  const [foldingState, setFoldingState] = useState('expanded'); // Track folding state
  
  // Conflict resolution modal state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [failedFiles, setFailedFiles] = useState([]);
  
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const debounceTimerRef = useRef(null); // Debounce timer for expensive operations
  const currentFilePathRef = useRef(null); // Track current file path to prevent unnecessary reloads
  const lastScrolledLineRef = useRef(null); // Track last scrolled line to detect changes
  const mermaidInitialized = useRef(false); // Track if Mermaid has been initialized

  // Helper to load file content without polluting the undo stack.
  // @monaco-editor/react controlled mode applies value-prop changes via executeEdits(),
  // which creates undo entries. When loading a new file the editor briefly holds ''
  // (initial useState), so the '' → file-content transition lands in the undo history.
  // Pressing CMD/CTRL+Z then undoes that transition, emptying the editor.
  // Fix: set the content directly on the Monaco model first (model.setValue clears the
  // undo stack), then update React state. Because the model already holds the new value
  // by the time @monaco-editor/react's effect runs, it finds getValue() === value and
  // skips its executeEdits() call, leaving the undo stack clean.
  const setFileContent = (content) => {
    editorRef.current?.getModel()?.setValue(content);
    setValue(content);
  };

  // Real-time collaboration integration (only if feature is enabled)
  const enableCollaboration = localStorage.getItem('enableCollaboration') === 'true';
  const collaborationEnabled = enableCollaboration && projectId && serviceName && file;
  
  const { isApplyingRemoteUpdate } = useCollaboration(
    collaborationEnabled ? editorRef.current : null,
    collaborationEnabled ? monacoRef.current : null,
    collaborationEnabled ? file : null,
    collaborationEnabled ? projectId : null,
    collaborationEnabled ? serviceName : null
  ) || {};

  // Watch for app theme changes and update Monaco theme
  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = getAppTheme();
      setTheme(newTheme);
      
      // Update Mermaid theme if initialized
      if (mermaidInitialized.current) {
        const isDark = newTheme === 'vs-dark';
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? 'dark' : 'default',
          securityLevel: 'loose',
          suppressErrorRendering: true,
          suppressErrors: true,
          logLevel: 'fatal',
        });
      }
    };

    // Listen for storage events (theme changes)
    window.addEventListener('storage', handleThemeChange);
    // Custom event for same-tab theme changes
    window.addEventListener('themechange', handleThemeChange);

    return () => {
      window.removeEventListener('storage', handleThemeChange);
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  // Initialize Mermaid library
  useEffect(() => {
    if (!mermaidInitialized.current && showMarkdownPreview) {
      const isDark = theme === 'vs-dark';
      mermaid.initialize({
        startOnLoad: false,
        theme: isDark ? 'dark' : 'default',
        securityLevel: 'loose',
        suppressErrorRendering: true,
        suppressErrors: true,
        logLevel: 'fatal',
        themeVariables: {
          darkMode: true,
          background: '#1e1e1e',
          primaryColor: '#4fc3f7',
          primaryTextColor: '#d4d4d4',
          primaryBorderColor: '#3e3e42',
          lineColor: '#858585',
          secondaryColor: '#2d2d30',
          tertiaryColor: '#252526'
        },
        flowchart: {
          htmlLabels: true,
          curve: 'basis',
          padding: 20,
          nodeSpacing: 80,
          rankSpacing: 80,
          diagramPadding: 20,
          useMaxWidth: true,
          defaultRenderer: 'dagre-wrapper'
        }
      });
      
      mermaidInitialized.current = true;
      
      // Render any existing mermaid diagrams
      setTimeout(() => {
        try {
          mermaid.run();
        } catch (e) {
          console.error('Mermaid initialization render error:', e);
        }
      }, 100);
    }
  }, [showMarkdownPreview]);

  // Re-render Mermaid diagrams when content changes - simplified approach matching chat frontend
  useEffect(() => {
    if (!showMarkdownPreview || !mermaidInitialized.current) return;

    const renderDiagrams = async () => {
      const mermaidElements = document.querySelectorAll('.markdown-preview-content .mermaid:not([data-processed="true"])');
      
      for (const el of mermaidElements) {
        try {
          // Get diagram text from element (trimmed)
          const diagramText = (el.textContent || el.innerText).trim();
          if (!diagramText) continue;
          
          // Generate unique ID
          const diagramId = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
          
          // Render diagram directly (no temp div needed)
          const { svg } = await mermaid.render(diagramId, diagramText);
          
          // Replace element content with SVG (same as chat implementation)
          el.innerHTML = svg;
          el.setAttribute('data-processed', 'true');
        } catch (e) {
          console.error('Mermaid render error:', e);
          
          // User-friendly error display
          let errorMsg = e.message || e.str || 'Failed to render diagram';
          el.innerHTML = `<div style="color: #f48771; padding: 20px; border: 1px solid #f48771; border-radius: 4px; background: rgba(244, 135, 113, 0.1); font-family: 'Consolas', 'Monaco', monospace;">
            <strong>⚠️ Mermaid Diagram Error</strong><br/>
            <span style="font-size: 12px; opacity: 0.8; margin-top: 8px; display: block;">${errorMsg}</span>
            <details style="margin-top: 12px; font-size: 11px; opacity: 0.7;">
              <summary style="cursor: pointer; user-select: none;">Show diagram source</summary>
              <pre style="margin-top: 8px; white-space: pre-wrap; word-wrap: break-word;">${el.textContent}</pre>
            </details>
          </div>`;
          el.setAttribute('data-processed', 'error');
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(renderDiagrams, 100);
    return () => clearTimeout(timer);
  }, [showMarkdownPreview, value]);

  // Handle resize events to re-render Mermaid diagrams
  useEffect(() => {
    if (!showMarkdownPreview || !mermaidInitialized.current) return;

    const markdownPreview = document.querySelector('.monaco-markdown-preview');
    if (!markdownPreview) return;

    // Create ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver((entries) => {
      // Debounce the re-render to avoid excessive calls
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        if (mermaidInitialized.current) {
          // Get all successfully processed diagrams
          const processedDiagrams = document.querySelectorAll('.mermaid[data-processed="true"]');
          
          for (const el of processedDiagrams) {
            try {
              // Get original diagram text (stored before rendering)
              const diagramText = el.getAttribute('data-diagram-source');
              if (!diagramText) continue;
              
              // Clear and re-render
              el.removeAttribute('data-processed');
              const diagramId = `mermaid-resize-${Math.random().toString(36).substr(2, 9)}`;
              const { svg } = await mermaid.render(diagramId, diagramText);
              el.innerHTML = svg;
              el.setAttribute('data-processed', 'true');
            } catch (e) {
              console.error('Mermaid resize render error:', e);
            }
          }
        }
      }, 300); // 300ms debounce
    });

    // Observe the preview container
    resizeObserver.observe(markdownPreview);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [showMarkdownPreview]);

  // Parse diff-style content and merge into existing editor content







  // Manual review mode removed - all changes auto-applied via external diff parser

  // Detect language from file extension
  const detectLanguage = (filename) => {
    if (!filename) return 'plaintext';
    
    const ext = filename.split('.').pop()?.toLowerCase();
    const languageMap = {
      // JavaScript/TypeScript
      js: 'javascript',
      jsx: 'javascript',
      ts: 'typescript',
      tsx: 'typescript',
      mjs: 'javascript',
      cjs: 'javascript',
      
      // Web
      html: 'html',
      htm: 'html',
      css: 'css',
      scss: 'scss',
      sass: 'sass',
      less: 'less',
      
      // Data formats
      json: 'json',
      yaml: 'yaml',
      yml: 'yaml',
      xml: 'xml',
      svg: 'xml',
      toml: 'toml',
      
      // Markdown & Docs
      md: 'markdown',
      mdx: 'markdown',
      txt: 'plaintext',
      
      // Programming languages
      py: 'python',
      java: 'java',
      c: 'c',
      cpp: 'cpp',
      cs: 'csharp',
      go: 'go',
      rs: 'rust',
      rb: 'ruby',
      php: 'php',
      swift: 'swift',
      kt: 'kotlin',
      scala: 'scala',
      
      // Shell & Config
      sh: 'shell',
      bash: 'shell',
      zsh: 'shell',
      fish: 'shell',
      dockerfile: 'dockerfile',
      
      // SQL
      sql: 'sql',
      
      // Other
      graphql: 'graphql',
      proto: 'protobuf',
      r: 'r',
      lua: 'lua'
    };
    
    // Check for special filenames
    const specialFiles = {
      'dockerfile': 'dockerfile',
      'docker-compose.yml': 'yaml',
      'docker-compose.yaml': 'yaml',
      'makefile': 'makefile',
      '.gitignore': 'plaintext',
      '.env': 'plaintext',
      'package.json': 'json',
      'tsconfig.json': 'json',
      'jsconfig.json': 'json',
      // Helm chart special files — use yaml as base language
      'chart.yaml': 'yaml',
      'chart.lock': 'yaml',
      'values.yaml': 'yaml',
      'values.schema.json': 'json',
      '_helpers.tpl': 'yaml',
      'notes.txt': 'plaintext',
    };

    // Helm template files (*.tpl, and files under templates/ or crds/)
    const lowerFilename = filename.toLowerCase();
    const isHelmTemplate = lowerFilename.endsWith('.tpl') ||
      lowerFilename.includes('templates/') ||
      lowerFilename.includes('crds/');
    if (isHelmTemplate) {
      return 'yaml';
    }

    if (specialFiles[lowerFilename]) {
      return specialFiles[lowerFilename];
    }
    
    return languageMap[ext] || 'plaintext';
  };

  /**
   * Handle user confirming manual resolution of conflict
   */
  const handleManualResolve = useCallback(() => {
    setShowConflictModal(false);
    
    if (!conflictData) return;
    
    const { targetPath } = conflictData;
    
    // Show a notification to the user about manual resolution
    console.log(`📝 User will manually resolve conflict for ${targetPath}`);
    
    // Dispatch event to notify that manual resolution is needed
    window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
      detail: {
        projectId: conflictData.targetProjectId,
        serviceName: conflictData.targetServiceName,
        filePath: targetPath,
        error: conflictData.error,
        manualResolutionRequested: true
      }
    }));
    
    // Clear state
    setConflictData(null);
    setFailedFiles([]);
    setShowDiffLoader(false);
  }, [conflictData]);
  
  /**
   * Collapse all code sections
   */
  const collapseAll = useCallback(() => {
    if (!editorRef.current) return;
    
    const editor = editorRef.current;
    editor.trigger('fold', 'editor.foldAll', {});
    setFoldingState('collapsed');
  }, []);

  /**
   * Expand all code sections
   */
  const expandAll = useCallback(() => {
    if (!editorRef.current) return;
    
    const editor = editorRef.current;
    editor.trigger('unfold', 'editor.unfoldAll', {});
    setFoldingState('expanded');
  }, []);

  /**
   * Handle user canceling conflict resolution
   */
  const handleConflictCancel = useCallback(() => {
    setShowConflictModal(false);
    
    if (!conflictData) return;
    
    const { targetPath, targetProjectId, targetServiceName } = conflictData;
    
    console.log(`❌ User canceled conflict resolution for ${targetPath}`);
    
    // Dispatch event to notify that diff failed and was canceled
    window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
      detail: {
        projectId: targetProjectId,
        serviceName: targetServiceName,
        filePath: targetPath,
        error: conflictData.error,
        userCanceled: true
      }
    }));
    
    // Clear state
    setConflictData(null);
    setFailedFiles([]);
    setShowDiffLoader(false);
  }, [conflictData]);

  /**
   * Apply diff to a non-open file in the background
   * Loads file content from IndexedDB, applies diff, saves back to IndexedDB
   */
  const applyDiffInBackground = useCallback(async (diffContent, targetPath, targetProjectId, targetServiceName) => {
    try {
      // Show diff parser loader
      const fileName = targetPath.split('/').pop();
      setDiffLoaderFileName(fileName);
      setDiffProgressInfo({
        stage: 'Initializing',
        operation: 'Parsing diff',
        llmActive: false
      });
      setShowDiffLoader(true);
      
      // Dispatch loading event
      window.dispatchEvent(new CustomEvent('indexedDBLoading', {
        detail: {loading: true, message: `AI writing to ${fileName}...`}
      }));

      // eslint-disable-next-line no-console
      console.log(`🔧 Applying diff in background for: ${targetPath}`);
      
      // Parse the diff to get operation info
      setDiffProgressInfo(prev => ({...prev, stage: 'Parsing', operation: 'Analyzing diff format'}));
      const parsed = parseUnifiedDiff(diffContent);
      
      if (!parsed.isValid) {
        // eslint-disable-next-line no-console
        console.error(`❌ Invalid diff for ${targetPath}:`, parsed.error);
        return;
      }
      
      // Handle file deletion
      if (parsed.isFileDeletion) {
        // eslint-disable-next-line no-console
        console.log(`🗑️ Deleting file from IndexedDB: ${targetPath}`);
        setDiffProgressInfo(prev => ({...prev, stage: 'Deleting', operation: 'Removing file'}));
        
        await indexedDbFileStorage.deleteFile(targetProjectId, targetServiceName, targetPath);
        
        // Dispatch completion event with file info
        window.dispatchEvent(new CustomEvent('diffParserComplete', {
          detail: {
            filePath: targetPath,
            operation: 'delete',
            projectId: targetProjectId,
            serviceName: targetServiceName,
            files: [{
              path: targetPath,
              operation: 'delete',
              projectId: targetProjectId,
              serviceName: targetServiceName
            }]
          }
        }));
        
        // Show success notification
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            message: `File deleted: ${fileName}`,
            duration: 3000
          }
        }));
        
        // eslint-disable-next-line no-console
        console.log(`✅ File deleted from IndexedDB: ${targetPath}`);
        return;
      }
      
      // Get current content from IndexedDB only
      setDiffProgressInfo(prev => ({...prev, stage: 'Loading', operation: 'Reading file content'}));
      let currentContent = '';
      
      const stored = await indexedDbFileStorage.getFile(targetProjectId, targetServiceName, targetPath);
      if (stored && stored.content) {
        currentContent = stored.content;
        // eslint-disable-next-line no-console
        console.log(`📂 Loaded ${targetPath} from IndexedDB for background diff`);
      } else {
        // No stored content - assume empty (new file or not yet loaded)
        // eslint-disable-next-line no-console
        console.log(`🆕 No stored content for ${targetPath}, using empty base`);
        currentContent = '';
      }
      
      // Apply the diff with progress tracking
      setDiffProgressInfo(prev => ({...prev, stage: 'Applying', operation: 'Merging changes'}));
      const result = await applyUnifiedDiff(diffContent, currentContent, {
        onProgress: (progress) => {
          // Update progress info with detailed stage information
          setDiffProgressInfo({
            stage: progress.stage || 'Processing',
            operation: progress.message || progress.text || 'Applying changes',
            confidence: progress.confidence || null
          });
        }
      });
      
      // Check if diff application succeeded
      if (!result.success) {
        // eslint-disable-next-line no-console
        console.error(`❌ Failed to apply diff to ${targetPath}:`, result.error);
        // eslint-disable-next-line no-console
        console.error(`   Diff content length: ${diffContent.length} chars`);
        // eslint-disable-next-line no-console
        console.error(`   Current content length: ${currentContent.length} chars`);
        
        // Hide loader on failure
        setShowDiffLoader(false);
        
        // Show conflict resolution modal for user to handle
        setFailedFiles([targetPath]);
        setConflictData({
          diffContent,
          currentContent,
          targetPath,
          targetProjectId,
          targetServiceName,
          error: result.error
        });
        setShowConflictModal(true);
        
        // Dispatch event to notify that diff failed
        window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
          detail: {
            projectId: targetProjectId,
            serviceName: targetServiceName,
            filePath: targetPath,
            fileName: fileName,
            currentContent: currentContent,
            failedDiff: diffContent,
            error: result.error,
            isBackgroundDiff: true
          }
        }));
        
        return; // Exit early on failure
      }
      
      // Save the result to localStorage
      // fileName already declared at the start of this function
      setDiffProgressInfo(prev => ({...prev, stage: 'Saving', operation: 'Writing to storage', llmActive: false}));
      
      // Check if file is new (doesn't exist in IndexedDB or has no original content)
      const isNewFile = !stored || !stored.originalContent;
      
      // CRITICAL: Preserve the original content when applying diff
      // If file exists in IndexedDB, keep its original content
      // If new file, use currentContent (which is empty or initial state) as original
      const originalContentToSave = stored?.originalContent || currentContent;
      
      await indexedDbFileStorage.saveFile(
        targetProjectId,
        targetServiceName,
        targetPath,
        result.newContent,
        {
          originalContent: originalContentToSave,
          fileName: fileName,
          appliedInBackground: true,
          isNew: isNewFile
        }
      );
      
      // Update file state
      
      const currentState = await indexedDbFileStorage.getFileState(targetProjectId, targetServiceName, targetPath);
      
      // If this is a new file, dispatch fileOperation event to update tree
      if (isNewFile) {
        window.dispatchEvent(new CustomEvent('fileOperation', {
          detail: {
            operation: 'create',
            path: targetPath,
            content: result.newContent
          }
        }));
        
        // FIX #1: For NEW files, trigger immediate sync (no debounce)
        window.dispatchEvent(new CustomEvent('previewSyncNewFile', {
          detail: {
            projectId: targetProjectId,
            serviceName: targetServiceName,
            filePath: targetPath
          }
        }));
      }
      
      // Dispatch completion event for loader with file info
      window.dispatchEvent(new CustomEvent('diffParserComplete', {
        detail: {
          filePath: targetPath,
          operation: currentState === 'new' ? 'create' : 'update',
          projectId: targetProjectId,
          serviceName: targetServiceName,
          files: [{
            path: targetPath,
            operation: currentState === 'new' ? 'create' : 'update',
            projectId: targetProjectId,
            serviceName: targetServiceName
          }]
        }
      }));
      
      // Dispatch custom event for auto-sync and tree update
      window.dispatchEvent(new CustomEvent('monacoFileChanged', {
        detail: {
          projectId: targetProjectId,
          serviceName: targetServiceName,
          filePath: targetPath
        }
      }));
      
      // eslint-disable-next-line no-console
      console.log(`✅ Background diff applied and saved to localStorage: ${targetPath} (state: ${currentState})`);
      
      // Show success notification to user
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'success',
          message: `File updated in background: ${fileName}`,
          duration: 3000
        }
      }));
      
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`❌ Error applying diff in background for ${targetPath}:`, error);
      
      // Hide loader on error
      setShowDiffLoader(false);
      
      // Show error notification
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: `Failed to update ${targetPath}: ${error.message}`,
          duration: 5000
        }
      }));
    } finally {
      // Clear loading event
      window.dispatchEvent(new CustomEvent('indexedDBLoading', {
        detail: {loading: false, message: ''}
      }));
    }
  }, []);

  // Update value when file changes - check IndexedDB first
  useEffect(() => {
    const loadFileContent = async () => {
      // eslint-disable-next-line no-console
      console.log('📋 MonacoFileEditor received file:', {
        path: file?.path,
        isBinary: file?.isBinary,
        mimeType: file?.mimeType,
        hasBinaryUrl: !!file?.binaryUrl,
        binaryUrl: file?.binaryUrl?.substring(0, 50)
      });
      
      if (file?.content !== undefined && projectId && serviceName && file?.path) {
        // CRITICAL FIX: Only reload from IndexedDB when file path actually changes
        // This prevents losing focus when user is typing
        const hasFileChanged = currentFilePathRef.current !== file.path;
        
        if (!hasFileChanged) {
          // User is still on the same file - don't reload from IndexedDB
          // This prevents interrupting the user's typing
          return;
        }
        
        // File has changed - update the ref and load from IndexedDB
        currentFilePathRef.current = file.path;
        
        // Dispatch loading event
        window.dispatchEvent(new CustomEvent('indexedDBLoading', {
          detail: {loading: true, message: `Loading ${file.path.split('/').pop()} from IndexedDB...`}
        }));

        // Check if there are local modifications in IndexedDB
        const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
        const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
      
      if (stored && stored.content) {
        // Validate stored content before using it
        const storedLooksInvalid = stored.content.includes('npm install') && 
          stored.content.includes('yarn add') && 
          !stored.content.includes('import');
        
        if (storedLooksInvalid) {
          // eslint-disable-next-line no-console
          console.error('❌ CRITICAL: localStorage contains invalid content for', file.path);
          // eslint-disable-next-line no-console
          console.error('   Deleting corrupted localStorage entry and using server content');
          await indexedDbFileStorage.deleteFile(projectId, serviceName, file.path);
          setFileContent(file.content);
          setModified(false);
          setGitState(null);
          setPreviewState(null);
        } else {
          // Use locally stored content
          setFileContent(stored.content);
          setModified(stored.content !== file.content);
          setGitState(currentState?.gitState || null);
          setPreviewState(currentState?.previewState || null);
          // eslint-disable-next-line no-console
          console.log('📂 Loaded file from IndexedDB:', file.path, 
            'GitState:', currentState?.gitState,
            'PreviewState:', currentState?.previewState,
            'Content length:', stored.content.length,
            'Lines:', stored.content.split('\n').length,
            'Modified:', stored.content !== file.content);
        }
      } else {
        // Use server content
        setFileContent(file.content);
        setModified(false);
        setGitState(null);
        setPreviewState(null);
      }

        // Clear loading event
        window.dispatchEvent(new CustomEvent('indexedDBLoading', {
          detail: {loading: false, message: ''}
        }));
      } else if (file?.content !== undefined) {
        // Fallback when projectId/serviceName not provided
        setFileContent(file.content);
        setModified(false);
        setGitState(null);
        setPreviewState(null);
        currentFilePathRef.current = file?.path || null;
      }
      
    };
    
    loadFileContent();
  }, [file, projectId, serviceName]);

  // Separate effect for handling line number navigation (from search results)
  // This runs independently of file content loading to ensure scrolling works
  // even when the same file is already open
  useEffect(() => {
    const lineNumber = file?.lineNumber;
    const filePath = file?.path;
    
    // Only scroll if we have a line number and it's different from the last one we scrolled to
    if (lineNumber && editorRef.current && monacoRef.current) {
      // Check if this is a new line to scroll to
      const lineKey = `${filePath}:${lineNumber}`;
      if (lastScrolledLineRef.current === lineKey) {
        return; // Already scrolled to this line
      }
      
      // eslint-disable-next-line no-console
      console.log('📍 Attempting to scroll to line:', lineNumber);
      
      // Wait for editor to be ready with content
      const scrollToLine = () => {
        if (!editorRef.current) return;
        
        const model = editorRef.current.getModel();
        if (!model) {
          // Model not ready yet, try again
          setTimeout(scrollToLine, 50);
          return;
        }
        
        // Ensure line number is within valid range
        const lineCount = model.getLineCount();
        const targetLine = Math.min(Math.max(1, lineNumber), lineCount);
        
        // eslint-disable-next-line no-console
        console.log('📍 Scrolling to line:', targetLine, 'of', lineCount);
        
        // Reveal line in center and set cursor position
        editorRef.current.revealLineInCenter(targetLine);
        editorRef.current.setPosition({lineNumber: targetLine, column: 1});
        editorRef.current.focus();
        
        // Mark this line as scrolled
        lastScrolledLineRef.current = lineKey;
      };
      
      // Start attempting to scroll after a short delay to ensure content is loaded
      setTimeout(scrollToLine, 150);
    }
  }, [file?.lineNumber, file?.path]);

  // Clear editor state when switching services
  // Use ref to track previous values and only reset when they ACTUALLY change
  const prevProjectServiceRef = useRef({ projectId: null, serviceName: null });
  
  useEffect(() => {
    const prev = prevProjectServiceRef.current;
    
    // Only reset if the values have ACTUALLY changed (not just re-renders)
    const hasProjectChanged = prev.projectId !== null && prev.projectId !== projectId;
    const hasServiceChanged = prev.serviceName !== null && prev.serviceName !== serviceName;
    
    if ((hasProjectChanged || hasServiceChanged) && projectId && serviceName) {
      // eslint-disable-next-line no-console
      console.log('🔄 Service changed in MonacoFileEditor, refreshing state:', serviceName);
      setValue('');
      setModified(false);
      setGitState(null);
      setPreviewState(null);
      setIsAIEditing(false);
      setErrorCount(0);
      setWarningCount(0);
    }
    
    // Update ref with current values
    prevProjectServiceRef.current = { projectId, serviceName };
  }, [projectId, serviceName]);

  // Listen for git pull events and refresh content
  useEffect(() => {
    const handleGitPull = async (event) => {
      const {projectId: pulledProjectId, serviceName: pulledServiceName} = event.detail;
      
      // Only refresh if this editor's project/service was pulled
      if (projectId === pulledProjectId && serviceName === pulledServiceName && file?.path) {
        // eslint-disable-next-line no-console
        console.log('🔄 Git pull detected, refreshing editor content for:', file.path);
        
        // Get the fresh content from IndexedDB (git pull already saved it there)
        const freshFile = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
        
        if (freshFile && freshFile.content) {
          // Update editor with fresh content from remote
          setFileContent(freshFile.content);
          setModified(false);
          setGitState(GIT_STATE.ORIGINAL); // Mark as synced with remote
          setPreviewState(PREVIEW_STATE.NOT_SENT);

          // eslint-disable-next-line no-console
          console.log('✅ Editor content refreshed with latest from GitLab');
        } else {
          // File might have been deleted from remote, clear editor
          // eslint-disable-next-line no-console
          console.warn('⚠️ File no longer exists after git pull:', file.path);
        }
      }
    };

    window.addEventListener('gitPullCompleted', handleGitPull);
    
    return () => {
      window.removeEventListener('gitPullCompleted', handleGitPull);
    };
  }, [file, projectId, serviceName]);

  // Handle ESC key to exit fullscreen or close search panel
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (showSearchPanel) {
          setShowSearchPanel(false);
        } else if (isFullscreen) {
          setIsFullscreen(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen, showSearchPanel]);

  // Listen for external content update events (from AI)
  // OPTIMIZATION: Parallel processing - different files process concurrently,
  // same file updates are sequenced to maintain consistency
  useEffect(() => {
    const handleAIContentUpdate = (event) => {
      // Make handler non-async to allow parallel processing
      // Wrap async work in promise that executes without blocking
      const processUpdate = async () => {
        const {filePath, content, updateType, oldString, newString, isStreaming, diffContent, projectId: eventProjectId, serviceName: eventServiceName} = event.detail;
        
        try {
          // CRITICAL: For unified diffs, we need to parse them first to check for file creation/modification
          // File operations (create/delete/modify) must be processed even if no file is open
          if (updateType === 'diff' && diffContent) {
            // Check if this is a file creation diff (--- /dev/null or --- a//dev/null)
            const isFileCreation = /^---\s+(?:a\/)?\/dev\/null$/m.test(diffContent);
            // Check if this is a file deletion diff (+++ /dev/null or +++ b//dev/null)
            const isFileDeletion = /^\+\+\+\s+(?:b\/)?\/dev\/null$/m.test(diffContent);
            
            if (isFileCreation || isFileDeletion) {
              // eslint-disable-next-line no-console
              console.log(`🆕 Detected file ${isFileCreation ? 'creation' : 'deletion'} diff, using external parser`);
              
              // Use external parser to handle file creation/deletion
              // Extract target path using external parser
              const parsed = parseUnifiedDiff(diffContent);
              if (parsed.isValid) {
                if (parsed.operation === 'create') {
                  const result = await applyUnifiedDiff(diffContent, '');
                  if (result.success) {
                    window.dispatchEvent(new CustomEvent('fileOperation', {
                      detail: {
                        operation: 'create',
                        path: result.targetPath,
                        content: result.newContent,
                        rawDiff: diffContent
                      }
                    }));
                  } else {
                    // eslint-disable-next-line no-console
                    console.error(`❌ Failed to create file ${parsed.targetPath}:`, result.error);
                    
                    // Dispatch diffApplicationFailed for AI notification
                    window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
                      detail: {
                        projectId: projectId || eventProjectId,
                        serviceName: serviceName || eventServiceName,
                        filePath: parsed.targetPath,
                        fileName: parsed.targetPath.split('/').pop(),
                        currentContent: '',
                        currentLineCount: 0,
                        failedDiff: diffContent,
                        error: result.error,
                        isFileCreation: true
                      }
                    }));
                  }
                } else if (parsed.operation === 'delete') {
                  window.dispatchEvent(new CustomEvent('fileOperation', {
                    detail: {
                      operation: 'delete',
                      path: parsed.targetPath
                    }
                  }));
                }
              }
              
              // Don't process further - file operation handled
              return;
            }
            
            // For file modifications, check if this diff is for a different file
            // If so, we should still process it by saving to localStorage
            if (file && filePath && file.path !== filePath && file.name !== filePath) {
              // eslint-disable-next-line no-console
              console.log(`📝 Diff is for different file (${filePath}), current file is (${file.path}). Will apply in background.`);
              
              // Apply diff in background without opening the file
              applyDiffInBackground(diffContent, filePath, eventProjectId, eventServiceName);
              
              // Don't process further - background application handles it
              return;
            }
          }
          
          // Only update if this is the file being edited
          // More flexible path matching to handle various path formats
          const isCurrentFile = file && filePath && (
            file.path === filePath || 
            file.name === filePath ||
            file.path.endsWith(filePath) ||
            filePath.endsWith(file.name)
          );
          
          // eslint-disable-next-line no-console
          console.log(`🔍 MonacoFileEditor: File matching check for aiContentUpdate:`, {
            hasFile: !!file,
            eventFilePath: filePath,
            'file.path': file?.path,
            'file.name': file?.name,
            isCurrentFile,
            updateType,
            hasDiffContent: !!diffContent,
            eventProjectId,
            eventServiceName,
            currentProjectId: projectId,
            currentServiceName: serviceName
          });
          
          if (isCurrentFile) {
        setIsAIEditing(isStreaming);
        
        let finalContent = content;
        let modifiedLines = [];
        
        // Check for unified diff format first (preferred method)
        if (updateType === 'diff' && diffContent) {
          // eslint-disable-next-line no-console
          console.log('📊 Processing unified diff from AI - loading from IndexedDB');
          
          try {
            // Get current content from IndexedDB first (source of truth), then fall back to state
            let currentContent = value;
            
            if (projectId && serviceName && file?.path) {
              const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
              if (stored?.content) {
                currentContent = stored.content;
                // eslint-disable-next-line no-console
                console.log('📂 Using content from IndexedDB for diff base');
              }
            }
            
            // CRITICAL: Validate that we have the correct content before applying diff
            // eslint-disable-next-line no-console
            console.log('🔍 Validating current content before applying diff');
            // eslint-disable-next-line no-console
            console.log('   File:', file?.path);
            // eslint-disable-next-line no-console
            console.log('   Current file lines:', currentContent.split('\n').length);
            // eslint-disable-next-line no-console
            console.log('   Content preview (first 150 chars):', currentContent.substring(0, 150));
            
            // Check if content looks invalid (e.g., contains installation instructions instead of code)
            const looksLikeInstallInstructions = currentContent.includes('npm install') || currentContent.includes('yarn add');
            const looksLikeCode = currentContent.includes('import') || currentContent.includes('function') || currentContent.includes('const');
            
            if (looksLikeInstallInstructions && !looksLikeCode && file?.path) {
              // eslint-disable-next-line no-console
              console.warn('⚠️ DETECTED: Editor contains invalid content (installation instructions)');
              // eslint-disable-next-line no-console
              console.warn('   Attempting to reload correct content from localStorage or server...');
              
              // Try to get correct content from IndexedDB first
              const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
              if (stored && stored.content && stored.content !== currentContent) {
                // eslint-disable-next-line no-console
                console.log('✅ Loaded correct content from IndexedDB');
                currentContent = stored.content;
              } else if (file.content && file.content !== currentContent) {
                // eslint-disable-next-line no-console
                console.log('✅ Using file.content from props');
                currentContent = file.content;
              } else {
                // eslint-disable-next-line no-console
                console.error('❌ Could not find valid content - diff may fail');
              }
            }
            
            const result = await applyUnifiedDiff(diffContent, currentContent);
            
            // CRITICAL: Check if this is a multi-file diff
            if (result.isMultiFile && result.files && result.files.length > 0) {
              // eslint-disable-next-line no-console
              console.log(`🗂️  Multi-file diff detected with ${result.files.length} files - processing each separately`);
              
              // Process each file in the multi-file diff
              for (const fileInfo of result.files) {
                // eslint-disable-next-line no-console
                console.log(`📝 Processing ${fileInfo.operation} for ${fileInfo.targetPath}`);
                
                // Handle file deletion
                if (fileInfo.isFileDeletion) {
                  // eslint-disable-next-line no-console
                  console.log(`🗑️ Deleting file from IndexedDB: ${fileInfo.targetPath}`);
                  
                  if (projectId && serviceName) {
                    await indexedDbFileStorage.deleteFile(projectId, serviceName, fileInfo.targetPath);
                    
                    // Show success notification
                    window.dispatchEvent(new CustomEvent('showNotification', {
                      detail: {
                        type: 'success',
                        message: `File deleted: ${fileInfo.targetPath.split('/').pop()}`,
                        duration: 3000
                      }
                    }));
                    
                    // eslint-disable-next-line no-console
                    console.log(`✅ File deleted from IndexedDB: ${fileInfo.targetPath}`);
                  }
                  continue; // Skip to next file
                }
                
                // Check if this is the currently open file
                const isCurrentFile = file && (file.path === fileInfo.targetPath || file.name === fileInfo.targetPath);
                
                if (isCurrentFile) {
                  // Apply diff to current file in the editor
                  // eslint-disable-next-line no-console
                  console.log(`📂 Applying diff to currently open file: ${fileInfo.targetPath}`);
                  
                  // Recursively call applyUnifiedDiff for single file
                  const singleFileResult = await applyUnifiedDiff(fileInfo.diffContent, currentContent);
                  
                  if (singleFileResult.success && singleFileResult.newContent) {
                    setValue(singleFileResult.newContent);
                    setModified(true);
                    
                    if (projectId && serviceName && file?.path) {
                      // Preserve stored originalContent from IndexedDB
                      const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
                      const originalContentToSave = stored?.originalContent || file?.content || '';
                      
                      await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, singleFileResult.newContent, {
                        originalContent: originalContentToSave,
                        fileName: file.name
                      });
                      
                      const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
                      setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);

                      window.dispatchEvent(new CustomEvent('monacoFileChanged', {
                        detail: {projectId, serviceName, filePath: file.path}
                      }));
                      
                      // eslint-disable-next-line no-console
                      console.log('💾 Multi-file diff - current file auto-saved to IndexedDB:', file.path);
                    }
                  } else {
                    // eslint-disable-next-line no-console
                    console.error(`❌ Failed to apply diff to current file ${fileInfo.targetPath}:`, singleFileResult.error);
                  }
                } else {
                  // Apply diff to other file in background
                  // eslint-disable-next-line no-console
                  console.log(`📝 Applying diff in background for: ${fileInfo.targetPath}`);
                  applyDiffInBackground(fileInfo.diffContent, fileInfo.targetPath, eventProjectId, eventServiceName);
                }
              }
              
              // All files processed
              return;
            }
            
            // Single file diff - process normally
            if (result.success && result.newContent) {
              // eslint-disable-next-line no-console
              console.log(`✅ Diff applied successfully to ${file.path}`);
              
              // Save to IndexedDB immediately (source of truth)
              if (projectId && serviceName && file?.path) {
                // Update state after successful save
                setValue(result.newContent);
                setModified(true);
                
                // Preserve stored originalContent from IndexedDB
                const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
                const originalContentToSave = stored?.originalContent || file?.content || '';
                
                await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, result.newContent, {
                  originalContent: originalContentToSave,
                  fileName: file.name
                });
                
                const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
                setGitState(currentState?.gitState || null);
                setPreviewState(currentState?.previewState || null);

                // eslint-disable-next-line no-console
                console.log(`📢 Dispatching monacoFileChanged event for ${file.path} to update tree view, editor, and preview`);
                window.dispatchEvent(new CustomEvent('monacoFileChanged', {
                  detail: {projectId, serviceName, filePath: file.path}
                }));
                
                // eslint-disable-next-line no-console
                console.log('💾 Changes auto-saved to IndexedDB:', file.path);
              }
              
              return; // Exit early for diff handling
            } else {
              // eslint-disable-next-line no-console
              console.error('❌ Failed to apply diff:', result.error);
              // eslint-disable-next-line no-console
              console.error('💡 This usually means the diff is based on a different version of the file.');
              // eslint-disable-next-line no-console
              console.error('   Current file has', currentContent.split('\n').length, 'lines');
              // eslint-disable-next-line no-console
              console.error('💡 Dispatching event to request AI regenerate with correct content.');
              
              // Dispatch event to notify that diff failed and AI needs correct content
              window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
                detail: {
                  projectId,
                  serviceName,
                  filePath: file?.path,
                  fileName: file?.name,
                  currentContent: currentContent,
                  currentLineCount: currentContent.split('\n').length,
                  failedDiff: diffContent,
                  error: result.error
                }
              }));
              
              // Don't apply corrupted content - keep current content
              return;
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to parse/apply unified diff:', error);
            // Fall through to regular content update
          }
        }
        
        // Check if content looks like a diff but wasn't explicitly marked as one
        if (!isStreaming && content && eventProjectId === projectId && eventServiceName === serviceName) {
          // Check if content contains unified diff markers
          if (/^---\s+/m.test(content) && /^\+\+\+\s+/m.test(content)) {
            // eslint-disable-next-line no-console
            console.log('📄 Content appears to be a unified diff, processing with external parser');
            
            // Get current content from IndexedDB first (source of truth)
            let currentContent = value;
            
            if (projectId && serviceName && file?.path) {
              const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
              if (stored?.content) {
                currentContent = stored.content;
              }
            }
            
            // Validate content before applying diff
            const looksLikeInstallInstructions = currentContent.includes('npm install') || currentContent.includes('yarn add');
            const looksLikeCode = currentContent.includes('import') || currentContent.includes('function') || currentContent.includes('const');
            
            if (looksLikeInstallInstructions && !looksLikeCode && file?.path) {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Invalid content detected, reloading from storage');
              const stored = indexedDbFileStorage.getFile(projectId, serviceName, file.path);
              if (stored?.content) {
                currentContent = stored.content;
                setValue(stored.content);
              } else if (file?.content) {
                currentContent = file.content;
                setValue(file.content);
              }
            }
            
            const result = await applyUnifiedDiff(content, currentContent);
            
            // Check if this is a multi-file diff
            if (result.isMultiFile && result.files && result.files.length > 0) {
              // eslint-disable-next-line no-console
              console.log(`🗂️  Auto-detected multi-file diff with ${result.files.length} files`);
              
              // Process each file
              for (const fileInfo of result.files) {
                const isCurrentFile = file && (file.path === fileInfo.targetPath || file.name === fileInfo.targetPath);
                
                if (isCurrentFile) {
                  const singleFileResult = await applyUnifiedDiff(fileInfo.diffContent, currentContent);
                  
                  if (singleFileResult.success && singleFileResult.newContent) {
                    setValue(singleFileResult.newContent);
                    setModified(true);
                    
                    if (projectId && serviceName && file?.path) {
                      // Preserve stored originalContent from IndexedDB
                      const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
                      const originalContentToSave = stored?.originalContent || file?.content || '';
                      
                      indexedDbFileStorage.saveFile(projectId, serviceName, file.path, singleFileResult.newContent, {
                        originalContent: originalContentToSave,
                        fileName: file.name
                      });
                      
                      const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
                      setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
                      
                      window.dispatchEvent(new CustomEvent('monacoFileChanged', {
                        detail: {projectId, serviceName, filePath: file.path}
                      }));
                      
                      // eslint-disable-next-line no-console
                      console.log('💾 Multi-file diff - current file auto-saved:', file.path);
                    }
                  }
                } else {
                  // Apply other files in background
                  applyDiffInBackground(fileInfo.diffContent, fileInfo.targetPath, eventProjectId, eventServiceName);
                }
              }
              
              return; // Exit early
            }
            
            // Single file diff
            if (result.success && result.newContent) {
              setValue(result.newContent);
              setModified(true);
              
              // Save to localStorage
              if (projectId && serviceName && file?.path) {
                // Preserve stored originalContent from IndexedDB
                const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
                const originalContentToSave = stored?.originalContent || file?.content || '';
                
                indexedDbFileStorage.saveFile(projectId, serviceName, file.path, result.newContent, {
                  originalContent: originalContentToSave,
                  fileName: file.name
                });
                
                const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
                setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
                
                window.dispatchEvent(new CustomEvent('monacoFileChanged', {
                  detail: {projectId, serviceName, filePath: file.path}
                }));
                
                // eslint-disable-next-line no-console
                console.log('💾 Diff auto-saved to localStorage:', file.path);
              }
              
              return; // Exit early for diff
            } else {
              // eslint-disable-next-line no-console
              console.error('❌ Failed to apply diff:', result.error);
              // eslint-disable-next-line no-console  
              console.error('💡 Dispatching event to request AI regenerate with correct content.');
              
              // Dispatch event to notify that diff failed and AI needs correct content
              window.dispatchEvent(new CustomEvent('diffApplicationFailed', {
                detail: {
                  projectId,
                  serviceName,
                  filePath: file?.path,
                  fileName: file?.name,
                  currentContent: currentContent,
                  failedDiff: content,
                  error: result.error
                }
              }));
              
              // Don't apply corrupted content
              return;
            }
          }
          
          // Not a diff - apply as full content replacement
          // eslint-disable-next-line no-console
          console.log('📄 Full file update (non-diff content)');
          
          setValue(content);
          setModified(true);
          finalContent = content;
          
          // Save to localStorage
          if (projectId && serviceName && file?.path) {
            // Preserve stored originalContent from IndexedDB
            const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
            const originalContentToSave = stored?.originalContent || file?.content || '';
            
            indexedDbFileStorage.saveFile(projectId, serviceName, file.path, content, {
              originalContent: originalContentToSave,
              fileName: file.name
            });
            
            const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
            setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
            
            window.dispatchEvent(new CustomEvent('monacoFileChanged', {
              detail: {projectId, serviceName, filePath: file.path}
            }));
            
            // eslint-disable-next-line no-console
            console.log('💾 Full file update saved to localStorage:', file.path);
          }
        
        // Handle streaming or partial updates (replace operations)
        if (updateType === 'replace' && oldString && newString && editorRef.current) {
            const currentContent = editorRef.current.getValue();
            
            if (currentContent.includes(oldString)) {
              // Perform the replacement
              finalContent = currentContent.replace(oldString, newString);
              
              // Calculate which lines were modified for highlighting
              const oldStringLines = oldString.split('\n');
              const newStringLines = newString.split('\n');
              
              // Find the line where replacement occurred
              const replacementIndex = currentContent.indexOf(oldString);
              const linesBefore = currentContent.substring(0, replacementIndex).split('\n');
              const startLine = linesBefore.length;
              const endLine = startLine + newStringLines.length - 1;
              
              // Track modified lines for highlighting
              for (let i = startLine; i <= endLine; i++) {
                modifiedLines.push(i);
              }
              
              // eslint-disable-next-line no-console
              console.log(`🔧 Partial update: replaced ${oldStringLines.length} line(s) with ${newStringLines.length} line(s)`);
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Could not find oldString in current content, applying as full update');
              finalContent = content || currentContent;
            }
          }
          
          if (updateType === 'replace' || isStreaming) {
            setValue(finalContent);
          
            // Update the editor content if editor is mounted
            if (editorRef.current && monacoRef.current) {
              const currentPosition = editorRef.current.getPosition();
              const model = editorRef.current.getModel();
              
              // Use pushEditOperations to preserve undo/redo stack
              // This is CRITICAL - setValue() clears the undo stack!
              if (model) {
                const fullRange = model.getFullModelRange();
                editorRef.current.executeEdits('ai-update', [{
                  range: fullRange,
                  text: finalContent,
                  forceMoveMarkers: true
                }]);
                
                // Restore cursor position if not streaming (final update)
                if (!isStreaming && currentPosition) {
                  editorRef.current.setPosition(currentPosition);
                }
              }
              
              // Highlight modified lines
              if (modifiedLines.length > 0 && !isStreaming) {
                const decorations = modifiedLines.map(lineNumber => ({
                  range: new monacoRef.current.Range(lineNumber, 1, lineNumber, 1),
                  options: {
                    isWholeLine: true,
                    className: 'ai-modified-line',
                    glyphMarginClassName: 'ai-modified-glyph'
                  }
                }));
                
                // Apply decorations and remove them after 3 seconds
                const decorationIds = editorRef.current.deltaDecorations([], decorations);
                setTimeout(() => {
                  if (editorRef.current) {
                    editorRef.current.deltaDecorations(decorationIds, []);
                  }
                }, 3000);
                
                // eslint-disable-next-line no-console
                console.log(`✨ Highlighted ${modifiedLines.length} modified line(s)`);
              }
            }
          }
        }
        
        // CRITICAL: Save to IndexedDB when AI updates content
        if (projectId && serviceName && file?.path && !isStreaming) {
          if (finalContent) {
            setModified(true);
            
            // Preserve stored originalContent from IndexedDB
            const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
            const originalContentToSave = stored?.originalContent || file?.content || '';
            
            indexedDbFileStorage.saveFile(projectId, serviceName, file.path, finalContent, {
              originalContent: originalContentToSave,
              fileName: file.name
            });
            // Update file state
            const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
            setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
            
            // Dispatch custom event for auto-sync
            window.dispatchEvent(new CustomEvent('monacoFileChanged', {
              detail: {projectId, serviceName, filePath: file.path}
            }));
            
            // eslint-disable-next-line no-console
            console.log('💾 AI content saved to localStorage:', file.path);
          }
        }
        
        // Notify parent of content change
        if (onContentChange) {
          onContentChange({
            ...file,
            content: finalContent
          });
        }
      }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error in processUpdate:', error);
      }
    };
      
      // Execute async work without blocking (allows parallel processing)
      processUpdate().catch(error => {
        // eslint-disable-next-line no-console
        console.error('❌ Error processing AI content update:', error);
      });
    };

    window.addEventListener('aiContentUpdate', handleAIContentUpdate);
    
    // Listen for file operation events (create, delete, rename)
    const handleFileOperation = (event) => {
      const {operation, path, oldPath, newPath} = event.detail;
      
      // eslint-disable-next-line no-console
      console.log(`📁 File operation: ${operation}`, {path, oldPath, newPath});
      
      // These operations are handled by parent components
      // This is just for logging and potential UI updates
      if (operation === 'create') {
        // eslint-disable-next-line no-console
        console.log(`✨ Creating new file: ${path}`);
      } else if (operation === 'delete') {
        // eslint-disable-next-line no-console
        console.log(`🗑️ Deleting file: ${path}`);
      } else if (operation === 'rename') {
        // eslint-disable-next-line no-console
        console.log(`📝 Renaming file: ${oldPath} → ${newPath}`);
      }
    };
    
    window.addEventListener('fileOperation', handleFileOperation);
    
    return () => {
      window.removeEventListener('aiContentUpdate', handleAIContentUpdate);
      window.removeEventListener('fileOperation', handleFileOperation);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, onContentChange, projectId, serviceName]);

  const handleEditorBeforeMount = (monaco) => {
    // Prevent re-initialization if monaco is already configured
    if (monacoRef.current) {
      return;
    }
    
    // Store monaco reference early to prevent duplicate initialization
    monacoRef.current = monaco;
    
    // Determine ECMAScript target based on file type
    // React files (.jsx, .tsx) use ES2022
    // Node.js files (server-side .js, .ts, .mjs, .cjs) use ES2023
    const isReactFile = file?.name && /\.(jsx|tsx)$/i.test(file.name);
    const isNodeFile = file?.name && /\.(js|ts|mjs|cjs)$/i.test(file.name) && !isReactFile;
    
    // Select appropriate ES target:
    // - ES2022 for React files (supports all React 18 features)
    // - ES2023 for Node.js files (supports latest Node.js LTS features)
    // - ESLatest as fallback for other files
    let scriptTarget = monaco.languages.typescript.ScriptTarget.ESLatest;
    if (isReactFile) {
      scriptTarget = monaco.languages.typescript.ScriptTarget.ES2022;
    } else if (isNodeFile) {
      // Note: Monaco might not have ES2023 enum value, use ESLatest for ES2023 support
      scriptTarget = monaco.languages.typescript.ScriptTarget.ES2022; // Will update if ES2023 becomes available
    }
    
   // Configure TypeScript compiler options
  monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
    target: scriptTarget,
    allowNonTsExtensions: true,
    // FIX: moduleResolution must be NodeJs or Bundler, not ReactJSX
    moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs, 
    module: monaco.languages.typescript.ModuleKind.ESNext,
    noEmit: true,
    esModuleInterop: true,
    // This is the correct place for ReactJSX
    jsx: monaco.languages.typescript.JsxEmit.ReactJSX, 
    allowJs: true,
    allowSyntheticDefaultImports: true,
    resolveJsonModule: true,
    typeRoots: ['node_modules/@types'],
    // Add lib support for ES2022 and ES2023 features
    lib: isReactFile 
      ? ['ES2022', 'DOM', 'DOM.Iterable'] 
      : isNodeFile 
        ? ['ES2023'] 
        : ['ESNext', 'DOM', 'DOM.Iterable'],
    // Relax type checking for better dev experience
    skipLibCheck: true,
    noImplicitAny: false,
    strict: false
  });

  // Sync JavaScript defaults as well
  monaco.languages.typescript.javascriptDefaults.setCompilerOptions(
    monaco.languages.typescript.typescriptDefaults.getCompilerOptions()
  );

    // Enable diagnostics and semantic highlighting but suppress module resolution errors
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      // Suppress common import/module errors in browser environment
      diagnosticCodesToIgnore: [
        2304, // Cannot find name (e.g., 'React')
        2305, // Module has no exported member
        2307, // Cannot find module or its corresponding type declarations
        2318, // Cannot find global type 'ImportMeta'
        2322, // Type is not assignable to type
        2339, // Property does not exist on type
        2345, // Argument of type is not assignable to parameter of type
        2552, // Cannot find name. Did you mean...?
        2580, // Cannot find name 'console'
        2581, // Cannot find name 'window'
        2582, // Cannot find name 'navigator'
        2583, // Cannot find name 'Set'. Do you need to change your target library?
        2584, // Cannot find name 'document'. Do you need to change your target library?
        2585, // Cannot find name 'localStorage'
        2586, // Cannot find name 'sessionStorage'
        2587, // Cannot find name 'fetch'
        2588, // Cannot assign to read-only property
        2589, // Cannot find name 'Element'
        2697, // Async functions are only available when targeting ECMAScript 2015 or higher
        2711, // Dynamic import call returns a 'Promise' (needs ES2015 lib or Promise declaration)
        2769, // No overload matches this call
        7016, // Could not find a declaration file for module
        2875, // JSX tag requires module path 'react/jsx-runtime' (modern JSX transform)
      ]
    });

    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      // Suppress common import/module errors in browser environment
      diagnosticCodesToIgnore: [
        2304, // Cannot find name (e.g., 'React')
        2305, // Module has no exported member
        2307, // Cannot find module or its corresponding type declarations
        2318, // Cannot find global type 'ImportMeta'
        2322, // Type is not assignable to type
        2339, // Property does not exist on type
        2345, // Argument of type is not assignable to parameter of type
        2552, // Cannot find name. Did you mean...?
        2580, // Cannot find name 'console'
        2581, // Cannot find name 'window'
        2582, // Cannot find name 'navigator'
        2583, // Cannot find name 'Set'. Do you need to change your target library?
        2584, // Cannot find name 'document'. Do you need to change your target library?
        2585, // Cannot find name 'localStorage'
        2586, // Cannot find name 'sessionStorage'
        2587, // Cannot find name 'fetch'
        2588, // Cannot assign to read-only property
        2589, // Cannot find name 'Element'
        2697, // Async functions are only available when targeting ECMAScript 2015 or higher
        2711, // Dynamic import call returns a 'Promise' (needs ES2015 lib or Promise declaration)
        2769, // No overload matches this call
        7016, // Could not find a declaration file for module
        2875  // JSX tag requires module path 'react/jsx-runtime' (modern JSX transform)
      ]
    });

    // CRITICAL: Enable eager model sync for better syntax highlighting
    monaco.languages.typescript.typescriptDefaults.setEagerModelSync(true);
    monaco.languages.typescript.javascriptDefaults.setEagerModelSync(true);
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // Don't overwrite monacoRef if already set in beforeMount
    if (!monacoRef.current) {
      monacoRef.current = monaco;
    }
    
    // Ensure the model has the correct language and URI for TSX/JSX files
    const model = editor.getModel();
    if (model && file?.name) {
      // Create proper URI with file extension for Monaco to recognize file type
      const uri = monaco.Uri.parse(`file:///${file.path || file.name}`);
      
      // If the model URI doesn't match, create a new model with correct URI
      if (model.uri.toString() !== uri.toString()) {
        const newModel = monaco.editor.createModel(model.getValue(), language, uri);
        editor.setModel(newModel);
        model.dispose(); // Dispose old model
      }
    }
    
    // Configure editor options
    editor.updateOptions({
      fontSize: 14,
      fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Fira Mono', 'Droid Sans Mono', 'Source Code Pro', monospace",
      lineHeight: 21,
      renderLineHighlight: 'all',
      scrollBeyondLastLine: false,
      minimap: {
        enabled: true
      },
      folding: true,
      glyphMargin: true, // Enable glyph margin for diff icons
      lineNumbers: 'on',
      renderWhitespace: 'selection',
      scrollbar: {
        verticalScrollbarSize: 10,
        horizontalScrollbarSize: 10
      },
      bracketPairColorization: {
        enabled: true
      },
      guides: {
        bracketPairs: true,
        indentation: true
      },
      wordWrap: 'off',
      automaticLayout: true,
      'semanticHighlighting.enabled': true // Ensure semantic highlighting is enabled
    });

    // Add keyboard shortcuts
    if (!readOnly) {
      // Cmd+S / Ctrl+S to save
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        if (modified && onSave) {
          handleSave();
        }
      });
    }
    
    // Cmd+Shift+F / Ctrl+Shift+F to open search
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
      setShowSearchPanel(prev => !prev);
    });

    // Register custom hover provider for errors with "Ask AI" button
    const hoverProvider = monaco.languages.registerHoverProvider('*', {
      provideHover: (model, position) => {
        const markers = monaco.editor.getModelMarkers({ resource: model.uri });
        const marker = markers.find(m => {
          return m.startLineNumber <= position.lineNumber &&
                 m.endLineNumber >= position.lineNumber &&
                 m.startColumn <= position.column &&
                 m.endColumn >= position.column &&
                 m.severity === monaco.MarkerSeverity.Error;
        });

        if (!marker) return null;

        const errorText = marker.message;
        const errorCode = marker.code ? ` (${marker.code})` : '';
        const lineNumber = marker.startLineNumber;
        const column = marker.startColumn;
        
        // Get context (5 lines around error)
        const lineCount = model.getLineCount();
        const startLine = Math.max(1, lineNumber - 2);
        const endLine = Math.min(lineCount, lineNumber + 2);
        const contextLines = [];
        
        for (let i = startLine; i <= endLine; i++) {
          contextLines.push(model.getLineContent(i));
        }
        const codeContext = contextLines.join('\n');

        // Create hover content with Ask AI button
        const hoverContent = [
          { value: `**Error**${errorCode}: ${errorText}` },
          { value: '---' },
          {
            value: `[🤖 Ask AI to Fix This Error](command:askAIToFixError?${encodeURIComponent(JSON.stringify({
              error: errorText,
              code: marker.code,
              line: lineNumber,
              column: column,
              filePath: file?.path || file?.name,
              codeContext: codeContext
            }))})`
          }
        ];

        return {
          range: new monaco.Range(
            marker.startLineNumber,
            marker.startColumn,
            marker.endLineNumber,
            marker.endColumn
          ),
          contents: hoverContent
        };
      }
    });

    // Register command handler for Ask AI button
    const commandDisposable = editor.addCommand(0, (ctx, args) => {
      // This will be triggered by the command link in hover
    });

    // Listen for link clicks in hover
    editor.onMouseDown((e) => {
      console.log('🖱️ Monaco editor mouse down', e.target.type, 'Target:', monaco.editor.MouseTargetType.CONTENT_WIDGET);
      
      if (e.target.type === monaco.editor.MouseTargetType.CONTENT_WIDGET) {
        console.log('✅ Content widget clicked, checking href...', e.event.browserEvent.target);
        const commandMatch = e.event.browserEvent.target?.href?.match(/command:askAIToFixError\?(.+)/);
        console.log('🔍 Command match:', commandMatch);
        
        if (commandMatch) {
          console.log('🎯 Ask AI command detected!');
          e.event.preventDefault();
          e.event.stopPropagation();
          
          try {
            const errorData = JSON.parse(decodeURIComponent(commandMatch[1]));
            console.log('📦 Error data parsed:', errorData);
            
            // Create AI message prompt to fix the error
            const aiPrompt = `I have an error in my code that needs fixing:\n\nFile: ${errorData.filePath}\nLine: ${errorData.line}, Column: ${errorData.column}\nError: ${errorData.error}${errorData.code ? ` (${errorData.code})` : ''}\n\nCode context:\n\`\`\`\n${errorData.codeContext}\n\`\`\`\n\nPlease analyze this error and provide a fix.`;
            
            console.log('🤖 Dispatching sendAIMessage event with prompt:', aiPrompt);
            
            // Dispatch event to send to AI chat
            window.dispatchEvent(new CustomEvent('sendAIMessage', {
              detail: {
                message: aiPrompt,
                context: {
                  filePath: errorData.filePath,
                  errorLine: errorData.line,
                  errorColumn: errorData.column
                }
              }
            }));
            
            console.log('✅ sendAIMessage event dispatched');
            
            // Show notification
            window.dispatchEvent(new CustomEvent('showNotification', {
              detail: {
                type: 'info',
                message: '🤖 Asking AI to analyze and fix the error...',
                duration: 3000
              }
            }));
          } catch (error) {
            console.error('❌ Failed to parse error data:', error);
          }
        }
      }
    });
    
    // Monitor for marker changes (errors/warnings from LSP, linting, etc.)
    const updateMarkerCounts = () => {
      const model = editor.getModel();
      if (!model) return;
      
      const markers = monaco.editor.getModelMarkers({resource: model.uri});
      
      // Count errors and warnings
      const errors = markers.filter(m => m.severity === monaco.MarkerSeverity.Error).length;
      const warnings = markers.filter(m => m.severity === monaco.MarkerSeverity.Warning).length;
      
      setErrorCount(errors);
      setWarningCount(warnings);
      
      // Pass errors to parent component if callback provided
      if (onErrorsChange && file?.path) {
        const errorMarkers = markers.filter(m => m.severity === monaco.MarkerSeverity.Error);
        onErrorsChange(file.path, errorMarkers);
      }
    };
    
    // Initial marker count
    updateMarkerCounts();
    
    // Listen for marker changes
    const markersDisposable = monaco.editor.onDidChangeMarkers(() => {
      updateMarkerCounts();
    });
    
    // Cleanup on unmount
    return () => {
      markersDisposable.dispose();
    };
  };

  const handleChange = async (newValue) => {
    setValue(newValue);
    const isModified = newValue !== (file?.content || '');
    setModified(isModified);
    
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce expensive operations (IndexedDB save, event dispatch, tree refresh)
    debounceTimerRef.current = setTimeout(async () => {
      // Save to localStorage if projectId and serviceName are provided
      if (projectId && serviceName && file?.path) {
        if (isModified) {
          // Preserve stored originalContent from IndexedDB
          const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
          const originalContentToSave = stored?.originalContent || file?.content || '';
          
          await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, newValue, {
            originalContent: originalContentToSave,
            fileName: file.name
          });
          // Update file state
          const currentState = await indexedDbFileStorage.getFileState(projectId, serviceName, file.path);
          setGitState(currentState?.gitState || null);
                      setPreviewState(currentState?.previewState || null);
          
          // Send file update to collaborators if collaboration is enabled
          if (collaborationEnabled) {
            const syncService = (await import('../services/syncService')).default;
            if (syncService.isInitialized()) {
              // Don't send update if we're applying a remote update (prevents infinite loop)
              if (isApplyingRemoteUpdate && isApplyingRemoteUpdate()) {
                console.log('[MonacoFileEditor] Skipping file update send - applying remote update');
              } else {
                console.log('[MonacoFileEditor] Sending file update:', { 
                  filePath: file.path, 
                  fileName: file.name,
                  serviceName, 
                  contentLength: newValue?.length 
                });
                syncService.sendFileUpdate(file.path, serviceName, newValue);
              }
            }
          }
          
          // Dispatch custom event for auto-sync (debounced)
          window.dispatchEvent(new CustomEvent('monacoFileChanged', {
            detail: {projectId, serviceName, filePath: file.path}
          }));
        } else {
          // FIX: Content matches original after undo/manual edit
          // Don't delete the file from IndexedDB - just keep it with original content
          // This preserves the file entry and prevents deletion on undo
          const stored = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
          if (stored) {
            // File exists in IndexedDB - update it with the original content
            await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, newValue, {
              originalContent: stored.originalContent || file?.content || '',
              fileName: file.name
            });
          }
          setGitState(null);
          setPreviewState(null);
        }
        
        // Notify parent about modification status change
        if (onContentChange) {
          onContentChange({
            ...file,
            content: newValue,
            modified: isModified
          });
        }
      }
    }, 500); // 500ms debounce - balances responsiveness with performance
  };

  const handleSave = async () => {
    if (onSave && file) {
      onSave({
        ...file,
        content: value
      });
      
      setModified(false);
      
      // Remove from IndexedDB after successful save (with event notification)
      if (projectId && serviceName && file?.path) {
        await indexedDbFileStorage.deleteFile(projectId, serviceName, file.path);
      }
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };

  const toggleTheme = () => {
    // Update app theme, which will trigger the theme watcher
    const newAppTheme = theme === 'vs-dark' ? 'light' : 'dark';
    localStorage.setItem('theme', newAppTheme);
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('themechange'));
    window.dispatchEvent(new Event('storage'));
  };

  const formatDocument = () => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.formatDocument')?.run();
    }
  };

  const isPipelineFile = (filename) => {
    if (!filename) return false;
    const lowerName = filename.toLowerCase();
    
    // Check for specific pipeline files
    const pipelineFiles = [
      '.gitlab-ci.yml',
      'jenkinsfile',
      'azure-pipelines.yml',
      '.harness/'
    ];
    
    // Check if it's a GitHub Actions workflow
    if (lowerName.includes('.github/workflows/') && lowerName.endsWith('.yml')) {
      return true;
    }
    
    // Check for other pipeline files
    return pipelineFiles.some(pattern => lowerName.includes(pattern.toLowerCase()));
  };

  const handlePipelineInsert = async (pipelineData) => {
    const { filename, content, platform } = pipelineData;
    
    // Don't close the editor anymore - auto-save in background
    
    // Check if we're updating the current file or creating a new one
    const isUpdatingCurrent = file && (file.path === filename || file.name === filename);
    
    if (isUpdatingCurrent) {
      // Update current file
      setValue(content);
      setModified(true);
      
      // Save to IndexedDB if we have project context
      if (projectId && serviceName) {
        await indexedDbFileStorage.saveFile(projectId, serviceName, file.path, content, {
          originalContent: file.content || '',
          fileName: file.name
        });
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('monacoFileChanged', {
          detail: { projectId, serviceName, filePath: file.path }
        }));
      }
    } else {
      // Create new file event - dispatch to parent or handle locally
      const newFile = {
        path: filename,
        name: filename.split('/').pop(),
        content: content,
        type: 'file'
      };
      
      // Save to IndexedDB if we have project context
      if (projectId && serviceName) {
        await indexedDbFileStorage.saveFile(projectId, serviceName, filename, content, {
          originalContent: '',
          fileName: newFile.name
        });
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('pipelineFileCreated', {
          detail: { projectId, serviceName, file: newFile, platform }
        }));
      }
      
      // Update current editor content
      setValue(content);
      setModified(true);
    }
  };

  const printMarkdownPreview = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      // Get the already-rendered preview content (with rendered Mermaid diagrams)
      const previewContent = document.querySelector('.markdown-preview-content');
      const clonedContent = previewContent ? previewContent.cloneNode(true) : null;
      
      // If we have rendered content, use it; otherwise fall back to rendering fresh
      const renderedHtml = clonedContent ? clonedContent.innerHTML : renderMarkdown(value);
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${file.name} - Print Preview</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                font-size: 16px;
                line-height: 1.6;
                max-width: 900px;
                margin: 40px auto;
                padding: 20px;
                color: #333;
              }
              h1, h2, h3, h4, h5, h6 { color: #000; margin-top: 24px; }
              h1 { font-size: 2em; border-bottom: 1px solid #ddd; padding-bottom: 8px; }
              h2 { font-size: 1.5em; border-bottom: 1px solid #ddd; padding-bottom: 8px; }
              h3 { font-size: 1.25em; }
              code { background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-family: 'Monaco', 'Menlo', monospace; }
              pre { background: #f5f5f5; padding: 16px; border-radius: 6px; overflow-x: auto; border: 1px solid #ddd; }
              pre code { background: none; padding: 0; }
              a { color: #0366d6; text-decoration: none; }
              table { border-collapse: collapse; width: 100%; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
              th { background: #f5f5f5; font-weight: 600; }
              img { max-width: 100%; height: auto; }
              svg { max-width: 100%; height: auto; }
              @media print {
                body { margin: 0; padding: 20px; }
                svg { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            ${renderedHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const printHtmlPreview = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(value);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  const toggleSearchPanel = () => {
    setShowSearchPanel(prev => !prev);
  };

  const handleSearchFileSelect = async (filePath, lineNumber) => {
    try {
      // If it's a different file, dispatch event to open it
      if (file?.path !== filePath) {
        window.dispatchEvent(new CustomEvent('openFileFromSearch', {
          detail: {
            projectId,
            serviceName,
            filePath,
            lineNumber
          }
        }));
      } else {
        // Same file, just navigate to line
        if (editorRef.current && lineNumber) {
          editorRef.current.revealLineInCenter(lineNumber);
          editorRef.current.setPosition({lineNumber, column: 1});
          editorRef.current.focus();
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error opening file from search:', error);
    }
  };

  // Track markers (errors/warnings) for the current file when it changes
  useEffect(() => {
    // Wait for next tick to ensure editor is ready and model is set
    const timer = setTimeout(() => {
      if (!editorRef.current || !monacoRef.current || !file?.path) {
        return;
      }
      
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      const model = editor.getModel();
      
      if (!model) {
        return;
      }
      
      const markers = monaco.editor.getModelMarkers({resource: model.uri});
      
      // Count errors and warnings
      const errors = markers.filter(m => m.severity === monaco.MarkerSeverity.Error).length;
      const warnings = markers.filter(m => m.severity === monaco.MarkerSeverity.Warning).length;
      
      setErrorCount(errors);
      setWarningCount(warnings);
      
      // Pass errors to parent component if callback provided
      if (onErrorsChange) {
        const errorMarkers = markers.filter(m => m.severity === monaco.MarkerSeverity.Error);
        onErrorsChange(file.path, errorMarkers);
      }
    }, 100); // Small delay to ensure editor model is updated
    
    return () => clearTimeout(timer);
  }, [file?.path, onErrorsChange]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (!file) {
    return (
      <div className="monaco-editor-empty">
        <span className="empty-icon">{'📄'}</span>
        <p>{'Select a file from the repository to view its contents'}</p>
      </div>
    );
  }

  const language = detectLanguage(file.name);

  return (
    <div className={`monaco-editor-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="monaco-editor-header">
        <div className="monaco-file-info">
          <span className="monaco-file-icon">
            {language === 'javascript' ? '⚛️' : 
             language === 'typescript' ? '📘' : 
             language === 'python' ? '🐍' : 
             language === 'json' ? '📋' : 
             language === 'markdown' ? '📝' : '📄'}
          </span>
          <span className="monaco-file-path">{file.path}</span>
          {modified && <span className="monaco-modified-indicator">{'● Modified'}</span>}
          {previewState === PREVIEW_STATE.SENT && <span className="monaco-sent-indicator">{'✓ Sent'}</span>}
          {previewState === PREVIEW_STATE.OUTDATED && <span className="monaco-changed-after-sent-indicator">{'● Changed after sent'}</span>}
          {isAIEditing && <span className="monaco-ai-editing-indicator">{'🤖 AI Editing...'}</span>}
          {isFullscreen && (
            <button
              className="monaco-btn monaco-btn-close-fullscreen"
              onClick={toggleFullscreen}
              title="Exit fullscreen (ESC)"
              type="button"
              style={{marginLeft: '12px'}}
            >
              {'✕ Exit Fullscreen'}
            </button>
          )}
        </div>
        
        <div className="monaco-toolbar">
          <button
            className="monaco-btn"
            onClick={collapseAll}
            title="Collapse all code sections"
            type="button"
          >
            {'⊟ Collapse'}
          </button>
          
          <button
            className="monaco-btn"
            onClick={expandAll}
            title="Expand all code sections"
            type="button"
          >
            {'⊞ Expand'}
          </button>
          
          <button
            className="monaco-btn"
            onClick={toggleTheme}
            title={theme === 'vs-dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            type="button"
          >
            {theme === 'vs-dark' ? '☀️' : '🌙'}
          </button>
          
          <button
            className="monaco-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen (ESC)' : 'Enter fullscreen'}
            type="button"
            style={{fontSize: '20px', lineHeight: 1, padding: '4px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
          >
            {isFullscreen ? '⤓' : '⤢'}
          </button>
          
          {language === 'markdown' && (
            <>
              <button
                className={`monaco-btn ${showMarkdownPreview ? 'active' : ''}`}
                onClick={() => setShowMarkdownPreview(!showMarkdownPreview)}
                title={showMarkdownPreview ? 'Show editor' : 'Show preview'}
                type="button"
              >
                {showMarkdownPreview ? '📝 Edit' : '👁️ Preview'}
              </button>
              {showMarkdownPreview && (
                <button
                  className="monaco-btn"
                  onClick={printMarkdownPreview}
                  title="Print markdown preview"
                  type="button"
                >
                  {'🖨️ Print'}
                </button>
              )}
            </>
          )}
          
          {language === 'html' && (
            <>
              <button
                className={`monaco-btn ${showHtmlPreview ? 'active' : ''}`}
                onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                title={showHtmlPreview ? 'Show editor' : 'Show preview'}
                type="button"
              >
                {showHtmlPreview ? '📝 Edit' : '👁️ Preview'}
              </button>
              {showHtmlPreview && (
                <button
                  className="monaco-btn"
                  onClick={printHtmlPreview}
                  title="Print HTML preview"
                  type="button"
                >
                  {'🖨️ Print'}
                </button>
              )}
            </>
          )}
          
          {file?.name?.endsWith('.svg') && (
            <button
              className={`monaco-btn ${showSvgPreview ? 'active' : ''}`}
              onClick={() => setShowSvgPreview(!showSvgPreview)}
              title={showSvgPreview ? 'Show editor' : 'Show preview'}
              type="button"
            >
              {showSvgPreview ? '📝 Edit' : '👁️ Preview'}
            </button>
          )}
          
          {/* Pipeline Editor Button - show for YAML files or when explicitly pipeline files */}
          {(language === 'yaml' || isPipelineFile(file.name)) && (
            <button
              className={`monaco-btn ${showPipelineEditor ? 'active' : ''}`}
              onClick={() => setShowPipelineEditor(!showPipelineEditor)}
              title={showPipelineEditor ? 'Close pipeline editor' : 'Create pipeline'}
              type="button"
            >
              {showPipelineEditor ? '📝 Edit' : '🔧 Pipeline'}
            </button>
          )}
          
          <button
            className="monaco-btn"
            onClick={formatDocument}
            title="Format document"
            type="button"
          >
            {'✨ Format'}
          </button>
          
          <button
            className="monaco-btn"
            onClick={handleCopy}
            title="Copy all content"
            type="button"
          >
            {'📋 Copy'}
          </button>
          
          {!readOnly && onSave && (
            <button
              className="monaco-btn monaco-btn-primary"
              disabled={!modified}
              onClick={handleSave}
              title="Save file (Cmd+S)"
              type="button"
            >
              {'💾 Save'}
            </button>
          )}
        </div>
      </div>
      
      {showSearchPanel && (
        <MonacoSearchPanel
          onClose={() => setShowSearchPanel(false)}
          onFileSelect={handleSearchFileSelect}
          projectId={projectId}
          serviceName={serviceName}
        />
      )}
      
      <div className={`monaco-editor-wrapper ${showMarkdownPreview && showHtmlPreview ? 'split-preview' : ''}`}>
        {showPipelineEditor && (
          <PipelineEditor 
            onInsert={handlePipelineInsert}
            currentContent={value}
            filename={file.path || file.name}
          />
        )}
        
        {showMarkdownPreview && language === 'markdown' && (
          <div className="monaco-markdown-preview">
            <div className="markdown-preview-content" dangerouslySetInnerHTML={{__html: renderMarkdown(value)}} />
          </div>
        )}
        
        {showHtmlPreview && language === 'html' && (
          <div className="monaco-html-preview">
            <iframe
              title="HTML Preview"
              srcDoc={value}
              sandbox="allow-scripts allow-same-origin allow-forms"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#ffffff'
              }}
            />
          </div>
        )}
        
        {showSvgPreview && file?.name?.endsWith('.svg') && (
          <div className="monaco-svg-preview" style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme === 'vs-dark' ? '#1e1e1e' : '#ffffff',
            padding: '20px',
            overflow: 'auto'
          }}>
            <div 
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                border: '1px solid rgba(128, 128, 128, 0.3)',
                borderRadius: '4px',
                padding: '20px',
                backgroundColor: '#ffffff'
              }}
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </div>
        )}
        
        {/* Image preview for binary image files */}
        {(() => {
          const shouldShowImage = !!(file?.isBinary && file?.mimeType?.startsWith('image/') && file?.binaryUrl);
          
          if (shouldShowImage) {
            return (
              <div className="monaco-image-preview" style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme === 'vs-dark' ? '#1e1e1e' : '#ffffff',
                padding: '20px',
                overflow: 'auto'
              }}>
                <img 
                  src={file.binaryUrl} 
                  alt={file.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    border: '1px solid rgba(128, 128, 128, 0.3)',
                    borderRadius: '4px'
                  }}
                  onLoad={(e) => {
                    console.log('✅ Image loaded successfully:', {
                      naturalWidth: e.target.naturalWidth,
                      naturalHeight: e.target.naturalHeight,
                      src: e.target.src.substring(0, 50)
                    });
                  }}
                  onError={(e) => {
                    console.error('❌ Image failed to load - blob data may be corrupted:', {
                      src: e.target.src.substring(0, 60),
                      naturalWidth: e.target.naturalWidth,
                      naturalHeight: e.target.naturalHeight
                    });
                  }}
                />
              </div>
            );
          }
          return null;
        })()}
        
        {!showPipelineEditor && (!showMarkdownPreview || language !== 'markdown') && (!showHtmlPreview || language !== 'html') && (!showSvgPreview || !file?.name?.endsWith('.svg')) && !(file?.isBinary && file?.mimeType?.startsWith('image/') && file?.binaryUrl) && (
          <Editor
            key={`${file.path}-${language}`}
            beforeMount={handleEditorBeforeMount}
            height="100%"
            language={language}
            loading={
              <div className="monaco-loading">
                <span>{'⏳ Loading editor...'}</span>
              </div>
            }
            onChange={handleChange}
            onMount={handleEditorDidMount}
            onValidate={(markers) => {
              // Handle validation markers (errors/warnings)
              // This is called when Monaco's language service detects issues
            }}
            options={{
              readOnly,
              padding: {top: 16, bottom: 16},
              // Disable features that might conflict with React DevTools
              'bracketPairColorization.enabled': true,
              'guides.bracketPairs': true,
              // Formatting options - use 2 spaces
              tabSize: 2,
              insertSpaces: true,
              detectIndentation: false
            }}
            theme={theme}
            value={value}
          />
        )}
      </div>
      
      {file.size && (
        <div className="monaco-editor-footer">
          <div className="monaco-status-left">
            <span className="monaco-status-item">{language}</span>
            <span className="monaco-status-item">
              {`${value.split('\n').length} lines`}
            </span>
            <span className="monaco-status-item">
              {`${(file.size / 1024).toFixed(2)} KB`}
            </span>
          </div>
          <div className="monaco-status-right">
            {errorCount > 0 && (
              <span className="monaco-status-item monaco-error-count" title={`${errorCount} error${errorCount > 1 ? 's' : ''}`}>
                {'❌ '}
                {errorCount}
              </span>
            )}
            {warningCount > 0 && (
              <span className="monaco-status-item monaco-warning-count" title={`${warningCount} warning${warningCount > 1 ? 's' : ''}`}>
                {'⚠️ '}
                {warningCount}
              </span>
            )}
            {errorCount === 0 && warningCount === 0 && (
              <span className="monaco-status-item monaco-no-problems" title="No problems">
                {'✓ No problems'}
              </span>
            )}
            <span className="monaco-status-item">
              {file.encoding || 'UTF-8'}
            </span>
          </div>
        </div>
      )}
      
      {/* Diff Parser Loader */}
      <DiffParserLoader
        fileName={diffLoaderFileName}
        isVisible={showDiffLoader}
        onCancel={() => setShowDiffLoader(false)}
        progressInfo={diffProgressInfo}
      />
      
      {/* Conflict Resolution Modal */}
      <ConflictResolutionModal
        isOpen={showConflictModal}
        fileName={conflictData?.targetPath || ''}
        error={conflictData?.error}
        onManualResolve={handleManualResolve}
        onCancel={handleConflictCancel}
        failedFiles={failedFiles}
      />
    </div>
  );
};

MonacoFileEditor.propTypes = {
  file: PropTypes.shape({
    content: PropTypes.string,
    encoding: PropTypes.string,
    lineNumber: PropTypes.number,
    name: PropTypes.string,
    path: PropTypes.string,
    size: PropTypes.number
  }),
  isPreviewMode: PropTypes.bool,
  onContentChange: PropTypes.func,
  onErrorsChange: PropTypes.func,
  onSave: PropTypes.func,
  projectId: PropTypes.string,
  readOnly: PropTypes.bool,
  serviceName: PropTypes.string
};

MonacoFileEditor.defaultProps = {
  file: null,
  isPreviewMode: false,
  onContentChange: null,
  onErrorsChange: null,
  onSave: null,
  onWebLLMStatusChange: null,
  projectId: null,
  readOnly: false,
  serviceName: null
};

export default MonacoFileEditor;
